import type { ForemanParsedResponse } from '../types/foreman';

const BLOCK_NAMES = [
  'META', 'ITEMS', 'PRICING', 'ALERT', 'PROGRESS', 'ACTIONS',
  'CHOICES', 'CORRECTION', 'PRACTICE', 'LEARNING_REPORT',
] as const;

/**
 * Inner content of a `[FOREMAN_<name>]` block. Prefers the properly-closed form;
 * falls back to an UNCLOSED opening tag (content runs up to the next `[FOREMAN_`
 * marker or end of message). This means a missing or typo'd closing tag still
 * renders as a block instead of leaking the raw `[FOREMAN_ITEMS]` marker into the
 * visible text — which is exactly what happened in a live order-check. Returns
 * null when the block isn't present.
 */
function blockContent(text: string, name: string): string | null {
  const closed = text.match(new RegExp(`\\[FOREMAN_${name}\\]([\\s\\S]*?)\\[\\/FOREMAN_${name}\\]`));
  if (closed) return closed[1];
  const open = text.match(new RegExp(`\\[FOREMAN_${name}\\]([\\s\\S]*?)(?=\\[\\/?FOREMAN_[A-Z_]+\\]|$)`));
  return open ? open[1] : null;
}

/** Parse `key: value` lines (values may contain colons, e.g. URLs). */
function kv(block: string): Record<string, string> {
  const out: Record<string, string> = {};
  block.trim().split('\n').forEach((line) => {
    const [key, ...rest] = line.split(':');
    if (key && key.trim()) out[key.trim()] = rest.join(':').trim();
  });
  return out;
}

/** Parse Foreman's structured output blocks from a message */
export function parseForemanResponse(text: string): ForemanParsedResponse {
  const result: ForemanParsedResponse = {
    text: '',
    meta: null,
    items: null,
    pricing: null,
    alert: null,
    progress: null,
    actions: null,
    choices: null,
    correction: null,
    practice: null,
    learningReport: null,
  };

  // [FOREMAN_META]
  const metaC = blockContent(text, 'META');
  if (metaC) {
    const m = kv(metaC);
    result.meta = { confidence: m.confidence || '0', skill: m.skill || '', source: m.source || '' };
  }

  // [FOREMAN_ITEMS]
  const itemsC = blockContent(text, 'ITEMS');
  if (itemsC) {
    const items = itemsC
      .trim()
      .split('\n')
      .filter((l) => l.trim().startsWith('-'))
      .map((line) => {
        const [typePart, ...textParts] = line.replace(/^-\s*/, '').split('|');
        return {
          type: (typePart.replace('type:', '').trim() as 'ok' | 'warn' | 'info') || 'info',
          text: textParts.join('|').trim(),
        };
      });
    result.items = items.length ? items : null;
  }

  // [FOREMAN_PRICING]
  const pricingC = blockContent(text, 'PRICING');
  if (pricingC) {
    const lines = pricingC.trim().split('\n').filter((l) => l.includes('|'));
    if (lines.length) {
      result.pricing = {
        lines: lines.slice(0, -1).map((line) => {
          const [cat, amt, note] = line.split('|').map((s) => s.trim());
          return { cat: cat || '', amt: amt || '', note: note || '' };
        }),
        total: lines[lines.length - 1].split('|')[1]?.trim() || null,
      };
    }
  }

  // [FOREMAN_ALERT]
  const alertC = blockContent(text, 'ALERT');
  if (alertC) {
    const a = kv(alertC);
    result.alert = { type: a.type || 'info', title: a.title || '', text: a.text || '' };
  }

  // [FOREMAN_PROGRESS]
  const progressC = blockContent(text, 'PROGRESS');
  if (progressC) {
    const p = progressC
      .trim()
      .split('\n')
      .filter((l) => l.trim())
      .map((line) => ({ label: line.replace(/^[✓○]\s*/, '').trim(), done: line.trim().startsWith('✓') }));
    result.progress = p.length ? p : null;
  }

  // [FOREMAN_ACTIONS]
  const actionsC = blockContent(text, 'ACTIONS');
  if (actionsC) {
    const a = actionsC
      .trim()
      .split('\n')
      .filter((l) => l.trim().startsWith('-'))
      .map((l) => l.replace(/^-\s*/, '').trim());
    result.actions = a.length ? a : null;
  }

  // [FOREMAN_CORRECTION]
  const corrC = blockContent(text, 'CORRECTION');
  if (corrC) {
    const c = kv(corrC);
    result.correction = {
      field: c.field || '',
      original: c.original || '',
      corrected: c.corrected || '',
      context: c.context || '',
      corrected_by: c.corrected_by || '',
    };
  }

  // [FOREMAN_PRACTICE]
  const pracC = blockContent(text, 'PRACTICE');
  if (pracC) {
    const p = kv(pracC);
    result.practice = {
      title: p.title || '',
      category: p.category || '',
      priority: p.priority || 'standard',
      taught_by: p.taught_by || '',
    };
  }

  // [FOREMAN_LEARNING_REPORT]
  const learnC = blockContent(text, 'LEARNING_REPORT');
  if (learnC) {
    const l = kv(learnC);
    result.learningReport = {
      period: l.period || '',
      corrections: l.corrections || '0',
      practices: l.practices || '0',
      trend: l.trend || 'stable',
      accuracy: l.accuracy || '',
    };
  }

  // Clean text — strip every structured block (closed AND unclosed), then sweep
  // any residual bare markers (orphan opening/closing tags, unknown block types)
  // so a protocol tag can NEVER leak into the visible message.
  let clean = text;
  for (const n of BLOCK_NAMES) {
    clean = clean
      .replace(new RegExp(`\\[FOREMAN_${n}\\][\\s\\S]*?\\[\\/FOREMAN_${n}\\]`, 'g'), '')
      .replace(new RegExp(`\\[FOREMAN_${n}\\][\\s\\S]*?(?=\\[\\/?FOREMAN_[A-Z_]+\\]|$)`, 'g'), '');
  }
  clean = clean.replace(/\[\/?FOREMAN_[A-Z_]+\]/g, '');
  result.text = clean.trim();

  return result;
}
